import { useRef } from "react";
import {
  Button,
  Text,
  makeStyles,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import type { StagedFile } from "../useConversation";

const ACCEPT = ".png,.jpg,.jpeg,.webp,.gif,.pdf,.docx,.pptx";

const useStyles = makeStyles({
  panel: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    ...shorthands.padding("12px"),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.border("1px", "dashed", tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground2,
  },
  hint: { color: tokens.colorNeutralForeground3 },
  actions: { display: "flex", gap: "8px", flexWrap: "wrap" },
  files: { display: "flex", flexDirection: "column", rowGap: "6px" },
  fileRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    columnGap: "8px",
    ...shorthands.padding("4px", "8px"),
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  fileMeta: { display: "flex", alignItems: "center", columnGap: "8px" },
  thumb: {
    width: "32px",
    height: "32px",
    objectFit: "cover",
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
  },
});

export function UploadPanel({
  stagedFiles,
  onAddFiles,
  onRemoveFile,
  onSubmit,
  disabled,
}: {
  stagedFiles: StagedFile[];
  onAddFiles: (files: File[]) => void;
  onRemoveFile: (index: number) => void;
  onSubmit: () => void;
  disabled?: boolean;
}) {
  const styles = useStyles();
  const inputRef = useRef<HTMLInputElement>(null);

  const handlePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length) onAddFiles(files);
    e.target.value = ""; // allow re-picking the same file
  };

  return (
    <div className={styles.panel}>
      <Text weight="semibold">📎 Partagez vos inspirations, visuels ou documents utiles</Text>
      <Text size={200} className={styles.hint}>
        Les fichiers restent locaux et sont envoyés au stockage final au moment de la validation
        du brief.
      </Text>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        onChange={handlePick}
        style={{ display: "none" }}
      />
      <div className={styles.actions}>
        <Button appearance="secondary" onClick={() => inputRef.current?.click()} disabled={disabled}>
          Choisir des fichiers
        </Button>
        <Button
          appearance="primary"
          onClick={onSubmit}
          disabled={disabled || stagedFiles.length === 0}
        >
          Envoyer {stagedFiles.length > 0 ? `(${stagedFiles.length})` : ""}
        </Button>
      </div>

      {stagedFiles.length > 0 && (
        <div className={styles.files}>
          {stagedFiles.map((f, i) => (
            <div key={`${f.name}_${f.size}`} className={styles.fileRow}>
              <span className={styles.fileMeta}>
                {f.previewUrl && <img className={styles.thumb} src={f.previewUrl} alt={f.name} />}
                <Text size={200}>
                  {f.name} — {f.size} octets
                </Text>
              </span>
              <Button
                size="small"
                appearance="subtle"
                aria-label={`Retirer ${f.name}`}
                onClick={() => onRemoveFile(i)}
                disabled={disabled}
              >
                ❌
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
